import store from '@/store'
import { BaseApiService } from '@/services/BaseApiService'

import {
  Nationality,
  RegistrationData,
  RegistrationTsSurveyData,
  RequestResetPasswordData,
  ResetPasswordData,
  SignInData,
  UpdatePasswordData,
  UpdateUserData,
  User
} from '@/interfaces/UserInterfaces'
import LocaleHelper from '@/utils/LocaleHelper'
import { EventBus } from '@/main'
import i18n from '@/i18n'
import VueI18n from 'vue-i18n'

class UserService extends BaseApiService {
  private readonly i18n: VueI18n;

  constructor () {
    super()
    this.i18n = i18n
  }

  checkEmailAvailability (email: string) : boolean {
    return this.callMethod('get', '/public/check-email-availability', {},
      'available', { params: { email } })
  }

  async checkCandidateNumberAvailability(candidateNumber: string | null): Promise<boolean> {
    return this.callMethod(
      'get',
      '/public/check-candidate-number-availability',
      {},
      'available',
      { params: { candidateNumber } }
    )
  }

  getAvailableRegistrationFields () : string[] {
    return this.callMethod('get', '/public/registration-fields', {}, 'fields')
  }

  async getUser (hard: boolean = false) : Promise<User> {
    if (store.getters['user/currentUser'] && hard !== true) {
      return store.getters['user/currentUser']
    }

    const response = await this.api.get('/me')
    store.commit('user/setUser', response.data)
    store.commit('user/setIsAuthenticated', true)

    return response.data
  }

  async completeLogin () {
    const userData = await this.getUser(true)
    LocaleHelper.setUserLocale(userData.locale)

    const userLocale = LocaleHelper.getUserLocale()
    if (userLocale) {
      this.i18n.locale = userLocale
    }

    EventBus.$emit('authorizedComplete')
  }

  async impersonate (token: string, user: string) {
    return this.api.post(`/impersonate?_switch_user=${user}`, {}, { headers: { 'X-DISPOSABLE-TOKEN': token } })
  }

  logout () : void {
    store.commit('user/logout')
  }

  registration (regData: RegistrationData) : object {
    console.log(regData);
    return this.callMethod('post', '/public/registration', { registrationForm: regData })
  }

  tsSurveyRegistration (regData: RegistrationTsSurveyData) : object {
    return this.callMethod('post', '/public/ts/registration', { registrationForm: regData })
  }

  signIn (signInData: SignInData) : string {
    return this.callMethod('post', '/login_check', signInData, 'token')
  }

  update (userId: number, userData: UpdateUserData) : User {
    return this.callMethod('put', `/user/${userId}`, { updateUserDataForm: userData })
  }

  changePassword (userId: number, passwordData: UpdatePasswordData) {
    return this.callMethod(
      'post',
      `/user/${userId}/change_password`,
      { updatePasswordForm: passwordData }
    )
  }

  requestResetPassword (passwordData: RequestResetPasswordData): boolean {
    return this.callMethod('post', '/public/request-reset-password', { passwordRequestForm: passwordData })
  }

  resetPassword (email: string, token: string, passwordData: ResetPasswordData): boolean {
    return this.callMethod('post', `public/reset-password/${email}/${token}`, { passwordResetForm: passwordData })
  }

  async uploadImage (userId: number, data: FormData) {
    return this.callMethod('post', `/user/${userId}/upload_picture`, data)
  }

  async deleteImage (userId: number, fileName: string) {
    return this.callMethod('delete', `/user/${userId}/delete_picture`, null, null, { data: { fileName } })
  }

  getNationalities (): Nationality[] {
    return this.callMethod('get', '/public/nationalities')
  }
}

export default new UserService()
